/**
 * マニュアルページ
 *
 * 管理画面内でマニュアルを閲覧するためのページ
 */

import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Flex, Title, Group, Box } from "@mantine/core";
import { Book } from "lucide-react";
import { ManualSidebar } from "../components/manual/ManualSidebar";
import { ManualContentViewer } from "../components/manual/ManualContentViewer";

/**
 * マニュアルページコンポーネント
 */
export function ManualPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState<{ title: string; path: string }[]>([]);

  // URLパラメータからパスを取得
  const selectedPath = searchParams.get("path");

  // パス選択時にURLを更新
  const setSelectedPath = useCallback(
    (path: string | null) => {
      if (path) {
        setSearchParams({ path });
      } else {
        setSearchParams({});
      }
    },
    [setSearchParams]
  );

  // マニュアルコンテンツを取得
  useEffect(() => {
    if (!selectedPath) {
      setContent(null);
      setBreadcrumbs([]);
      return;
    }

    const loadContent = async () => {
      setLoading(true);
      try {
        // public/manualフォルダからMarkdownファイルを取得
        const response = await fetch(`/manual/${selectedPath}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const md = await response.text();
        setContent(md);

        // パンくずリストを更新
        const parts = selectedPath.split("/");
        const crumbs = parts.map((part, index) => ({
          title: part.replace(".md", "").replace(/^\d+_/, ""),
          path: parts.slice(0, index + 1).join("/"),
        }));
        setBreadcrumbs(crumbs);
      } catch (error) {
        console.error("Failed to load manual content:", error);
        setContent("# エラー\n\nコンテンツの読み込みに失敗しました。");
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [selectedPath]);

  return (
    <Box
      style={{
        height: "calc(100vh - var(--app-shell-header-offset) - var(--app-shell-padding) * 2)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* ヘッダー */}
      <Group align="center" mb="md" style={{ flexShrink: 0 }}>
        <Book size={28} />
        <Title order={2}>マニュアル</Title>
      </Group>

      <Flex gap="md" h="100%" align="stretch" style={{ overflow: "hidden" }}>
        {/* 左サイドバー: 目次 */}
        <Box
          style={{
            width: 340,
            minWidth: 280,
            height: "100%",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ManualSidebar selectedPath={selectedPath} onSelectPath={setSelectedPath} />
        </Box>

        {/* 右側: コンテンツ表示エリア */}
        <Box
          style={{
            flex: 1,
            height: "100%",
            minWidth: 300,
            flexShrink: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ManualContentViewer
            content={content}
            loading={loading}
            breadcrumbs={breadcrumbs}
            selectedPath={selectedPath}
            onSelectPath={setSelectedPath}
          />
        </Box>
      </Flex>
    </Box>
  );
}
